/**
 * SidecarRunAnnotation
 *
 * Renders a persistent summary card for Sidecar delegation results inside the
 * parent assistant message.
 *
 * Primary responsibilities:
 * - Show Sidecar run status and merged answer
 * - Surface planned workers and worker outcomes
 * - Reopen the fixed Sidecar panel from chat history
 *
 * Dependencies:
 * - components/ai-elements/chain-of-thought
 * - components/ui/card
 * - types/annotation
 */

'use client'

import React, { useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, ExternalLink, Sparkles } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { Annotation, Annotation2 } from '@/types/annotation'
import { stripMarkdownForPreview } from '@/lib/strip-markdown-for-preview'
import { MessageMarkdown } from '@/components/messages/message-markdown'
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep
} from '@/components/ai-elements/chain-of-thought'

interface SidecarWorkerSummary {
  id?: string
  source?: 'ad_hoc' | 'built_in_helper' | 'custom_helper'
  helperTemplateId?: string
  helperId?: string
  title?: string
  objective?: string
  deliverable?: string
  status?: 'completed' | 'error' | 'interrupted'
  finalOutput?: string
  error?: string
}

interface SidecarRunAnnotationValue {
  runId?: string
  status?: 'planning' | 'running' | 'completed' | 'error' | 'cancelled'
  objective?: string
  reason?: string
  message?: string
  agentCount?: number
  successfulAgents?: number
  failedAgents?: number
  finalAnswer?: string
  usedDeterministicFallback?: boolean
  plannedWorkers?: Array<{
    id?: string
    source?: 'ad_hoc' | 'built_in_helper' | 'custom_helper'
    helperTemplateId?: string
    helperId?: string
    title?: string
    objective?: string
    deliverable?: string
  }>
  workers?: SidecarWorkerSummary[]
}

function getEffectiveSidecarStatus(
  value: SidecarRunAnnotationValue
): NonNullable<SidecarRunAnnotationValue['status']> {
  // A merged answer means the run delivered — never surface Error/Failed over
  // a usable finalAnswer just because one Helper had issues or a deadline fired.
  if (typeof value.finalAnswer === 'string' && value.finalAnswer.trim()) {
    return 'completed'
  }

  if (
    value.status === 'completed' ||
    value.status === 'error' ||
    value.status === 'cancelled'
  ) {
    return value.status
  }

  const workers = Array.isArray(value.workers) ? value.workers : []
  if (workers.length > 0) {
    const hasActiveWorker = workers.some(
      (worker) => worker.status !== 'completed' && worker.status !== 'error'
    )

    if (!hasActiveWorker) {
      const anySuccess = workers.some((worker) => worker.status === 'completed')
      return anySuccess ? 'completed' : value.status === 'error' ? 'error' : 'completed'
    }
  }

  return value.status ?? 'running'
}

function parseSidecarRunAnnotation(
  annotation: Annotation | Annotation2
): SidecarRunAnnotationValue | null {
  const raw = (annotation as Record<string, unknown>).sidecar_run

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }

  return raw as SidecarRunAnnotationValue
}

function getStatusBadge(status?: SidecarRunAnnotationValue['status']) {
  if (status === 'completed') {
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200"
      >
        Complete
      </Badge>
    )
  }

  if (status === 'error') {
    return (
      <Badge
        variant="outline"
        className="border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-200"
      >
        Error
      </Badge>
    )
  }

  if (status === 'cancelled') {
    return (
      <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
        Cancelled
      </Badge>
    )
  }

  return (
    <Badge
      variant="outline"
      className="border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-200"
    >
      Running
    </Badge>
  )
}

function openSidecarPanel(runId?: string) {
  window.dispatchEvent(
    new CustomEvent('openSidecarPanel', {
      detail: runId ? { runId } : {}
    })
  )
}

function getWorkerSourceLabel(source?: SidecarWorkerSummary['source']) {
  if (source === 'built_in_helper') return 'Built-in Helper'
  if (source === 'custom_helper') return 'Custom Helper'
  return 'Ad hoc'
}

export function SidecarRunAnnotation({ annotation }: { annotation: Annotation | Annotation2 }) {
  const sidecarRun = parseSidecarRunAnnotation(annotation)
  const [answerExpanded, setAnswerExpanded] = useState(false)

  const workerSummaries = useMemo(() => sidecarRun?.workers ?? [], [sidecarRun])

  if (!sidecarRun) return null

  const effectiveStatus = getEffectiveSidecarStatus(sidecarRun)

  const finalAnswer = sidecarRun.finalAnswer?.trim() ?? ''
  const canShowFinalAnswer = effectiveStatus === 'completed' && finalAnswer.length > 0
  const answerPlainText = stripMarkdownForPreview(finalAnswer)
  const shouldPreviewAnswer = !answerExpanded && answerPlainText.length > 220
  const answerPreview = shouldPreviewAnswer
    ? `${answerPlainText.slice(0, 220).trimEnd()}…`
    : answerPlainText
  const chainWorkers = sidecarRun.plannedWorkers?.length
    ? sidecarRun.plannedWorkers
    : workerSummaries.map((worker) => ({
        id: worker.id,
        source: worker.source,
        helperTemplateId: worker.helperTemplateId,
        helperId: worker.helperId,
        title: worker.title,
        objective: worker.objective,
        deliverable: worker.deliverable
      }))

  return (
    <Card className="border-border/70 bg-muted/20 p-4 shadow-none">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="text-primary size-4" />
            <span className="text-sm font-semibold">Sidecar</span>
            {getStatusBadge(effectiveStatus)}
          </div>
          {(sidecarRun.objective || sidecarRun.reason || sidecarRun.message) && (
            <p className="text-muted-foreground mt-2 text-xs leading-5">
              {sidecarRun.objective || sidecarRun.reason || sidecarRun.message}
            </p>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => openSidecarPanel(sidecarRun.runId)}
        >
          <ExternalLink className="mr-2 size-3.5" />
          Open
        </Button>
      </div>

      <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-2 text-xs">
        {typeof sidecarRun.agentCount === 'number' && <span>{sidecarRun.agentCount} helpers</span>}
        {typeof sidecarRun.successfulAgents === 'number' && (
          <span>• {sidecarRun.successfulAgents} helpers completed</span>
        )}
        {typeof sidecarRun.failedAgents === 'number' && sidecarRun.failedAgents > 0 && (
          <span>• {sidecarRun.failedAgents} helpers failed</span>
        )}
      </div>

      {canShowFinalAnswer && (
        <div className="border-border/70 bg-background/80 mt-3 rounded-lg border p-3">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-200">
            <CheckCircle2 className="size-3.5" />
            Merged answer
          </div>
          {shouldPreviewAnswer ? (
            <p className="text-foreground/80 text-sm leading-6">{answerPreview}</p>
          ) : (
            <div className="prose prose-sm text-foreground/90 dark:prose-invert max-w-none">
              <MessageMarkdown content={finalAnswer} />
            </div>
          )}
          {answerPlainText.length > 220 && (
            <button
              type="button"
              onClick={() => setAnswerExpanded((previous) => !previous)}
              className="mt-3 text-xs font-medium text-emerald-700 transition hover:text-emerald-800 dark:text-emerald-200 dark:hover:text-emerald-100"
            >
              {answerExpanded ? 'Show less' : 'Show merged answer'}
            </button>
          )}
        </div>
      )}

      {chainWorkers.length > 0 ? (
        <ChainOfThought
          className="border-border/70 bg-background/60 mt-3 rounded-lg border p-3"
          defaultOpen={effectiveStatus !== 'completed'}
        >
          <ChainOfThoughtHeader>Helper breakdown</ChainOfThoughtHeader>
          <ChainOfThoughtContent className="space-y-1 pl-1 pr-0">
            {chainWorkers.map((worker) => {
              const completedWorker = workerSummaries.find((summary) => summary.id === worker.id)
              const hasError = completedWorker?.status === 'error'
              const hasCompleted = completedWorker?.status === 'completed'

              return (
                <ChainOfThoughtStep
                  key={worker.id || worker.title}
                  icon={hasError ? AlertCircle : CheckCircle2}
                  label={worker.title || 'Helper'}
                  description={worker.objective}
                  status={hasError ? 'active' : hasCompleted ? 'complete' : 'pending'}
                >
                  <div className="text-muted-foreground mb-1 text-[11px]">
                    {getWorkerSourceLabel(worker.source)}
                  </div>
                  {worker.deliverable ? (
                    <div className="text-muted-foreground text-xs">
                      Deliverable: {worker.deliverable}
                    </div>
                  ) : null}
                  {completedWorker?.error ? (
                    <div className="text-xs text-rose-600 dark:text-rose-300">
                      {completedWorker.error}
                    </div>
                  ) : null}
                  {completedWorker?.finalOutput ? (
                    <div className="border-border/60 bg-muted/20 max-h-48 overflow-auto rounded-md border p-2 text-xs leading-5">
                      <MessageMarkdown content={completedWorker.finalOutput} />
                    </div>
                  ) : null}
                </ChainOfThoughtStep>
              )
            })}
          </ChainOfThoughtContent>
        </ChainOfThought>
      ) : null}
    </Card>
  )
}
